// Which set of the commissioned terrain a fight is drawn in (docs/166 B, docs/186): its kit, the World's
// biome and season, and day or night by the fight's hour. When the art lacks that exact set, the nearest one
// that exists stands in (another season, then day, then the temperate biome, then any biome of the kit), with
// a warning on the dev server; a kit with no art at all gets none, and the board keeps its Oryx look.
import atlas from 'virtual:fk-encounter-atlas';
import { phaseOf } from '../lighting/daylight';
import type { TimeOfDay } from './types';

const SEASONS = ['spring', 'summer', 'autumn', 'winter'] as const;
let available: ReadonlySet<string> | undefined;

/** Every set the build (or the dev server) has: `keep/temperate`, `forest/temperate/summer/day`, … */
export function setIdsAvailable(): ReadonlySet<string> {
  return (available ??= new Set(Object.keys(atlas.sets)));
}

/** What a fight asks for. `biome`/`season` as the World clock names them (any case); `hour` 0–24 decides day or night unless `time` is given. */
export interface SetWant { kit: string; biome?: string; season?: string; hour?: number; time?: TimeOfDay }
export interface SetChoice { id: string; wanted: string; fallback: boolean }

interface KitIndex {
  /** biome → indoor (one set) or the seasons and times it has. */
  biomes: Map<string, { indoor: boolean; seasons: Map<string, Set<TimeOfDay>> }>;
}
const indexes = new WeakMap<ReadonlySet<string>, Map<string, KitIndex>>();

function indexOf(avail: ReadonlySet<string>): Map<string, KitIndex> {
  let idx = indexes.get(avail);
  if (idx) return idx;
  idx = new Map();
  for (const id of avail) {
    const [kit, biome, season, time] = id.split('/');
    if (!kit || !biome) continue;
    let k = idx.get(kit);
    if (!k) idx.set(kit, (k = { biomes: new Map() }));
    let b = k.biomes.get(biome);
    if (!b) k.biomes.set(biome, (b = { indoor: false, seasons: new Map() }));
    if (season === undefined) { b.indoor = true; continue; }
    let times = b.seasons.get(season);
    if (!times) b.seasons.set(season, (times = new Set()));
    if (time === 'day' || time === 'night') times.add(time);
  }
  indexes.set(avail, idx);
  return idx;
}

/** The kit's biomes with art, alphabetically. */
export function biomesOf(kit: string, avail: ReadonlySet<string> = setIdsAvailable()): string[] {
  return [...(indexOf(avail).get(kit.toLowerCase())?.biomes.keys() ?? [])].sort();
}

/** The kit's seasons in a biome, spring first (empty for an indoor kit). */
export function seasonsOf(kit: string, biome: string, avail: ReadonlySet<string> = setIdsAvailable()): string[] {
  const b = indexOf(avail).get(kit.toLowerCase())?.biomes.get(biome.toLowerCase());
  return b ? SEASONS.filter((s) => b.seasons.has(s)) : [];
}

/** Indoors: one set per biome, no season or time of day. */
export function isIndoorKit(kit: string, avail: ReadonlySet<string> = setIdsAvailable()): boolean {
  const k = indexOf(avail).get(kit.toLowerCase());
  return !!k && [...k.biomes.values()].some((b) => b.indoor);
}

/** Day or night for a fight: the World chart's threshold (`phaseOf`, World.tsx); no hour is day. */
export function timeOfHour(hour: number | undefined): TimeOfDay {
  return hour !== undefined && phaseOf(hour) === 'night' ? 'night' : 'day';
}

const warned = new Set<string>();
function warnOnce(msg: string): void {
  if (!import.meta.env.DEV || warned.has(msg)) return;
  warned.add(msg);
  console.warn(msg);
}

/**
 * The set to draw a fight in, or undefined when the kit has no art at all. Fallbacks, in order: the same kit
 * and biome in another season (spring, summer, autumn, winter: the master first), the same set by day if its
 * night is missing, the kit in the temperate biome, then the kit's first biome alphabetically.
 */
export function chooseSet(want: SetWant, avail: ReadonlySet<string>): SetChoice | undefined {
  const kitKey = want.kit.toLowerCase();
  const kit = indexOf(avail).get(kitKey);
  if (!kit || !kit.biomes.size) return undefined;
  const biomes = [...kit.biomes.keys()].sort();
  const biome = want.biome?.toLowerCase() ?? (biomes.length === 1 ? biomes[0]! : 'temperate');
  const time = want.time ?? timeOfHour(want.hour);
  const asked = want.season?.toLowerCase();

  const masterOf = (b: string) => SEASONS.find((s) => kit.biomes.get(b)?.seasons.has(s));
  const inBiome = (b: string): string | undefined => {
    const bi = kit.biomes.get(b);
    if (!bi) return undefined;
    if (bi.indoor) return `${kitKey}/${b}`;
    const season = asked ?? masterOf(b);
    const order = [season, ...SEASONS.filter((s) => s !== season)].filter((s): s is string => !!s);
    const times: TimeOfDay[] = time === 'night' ? ['night', 'day'] : ['day'];
    for (const t of times) for (const s of order) if (bi.seasons.get(s)?.has(t)) return `${kitKey}/${b}/${s}/${t}`;
    return undefined;
  };

  const own = kit.biomes.get(biome);
  const wanted = own?.indoor || (!own && isIndoorKit(kitKey, avail))
    ? `${kitKey}/${biome}`
    : `${kitKey}/${biome}/${asked ?? masterOf(biome) ?? 'spring'}/${time}`;
  for (const b of [biome, 'temperate', biomes[0]!]) {
    const id = inBiome(b);
    if (!id) continue;
    const fallback = id !== wanted;
    if (fallback) warnOnce(`encounter art: no set ${wanted}; drawing ${id}`);
    return { id, wanted, fallback };
  }
  return undefined;
}

// ---- a place's country (docs/186 EA14) ----

/** The country kits (`Ground::kit`, crates/fk-schema/src/genesis.rs). */
export const COUNTRY_KITS: ReadonlySet<string> = new Set(['forest', 'autumn', 'winterwood', 'pinewood', 'mirewood', 'dunes', 'highland', 'jungle', 'underdark', 'ashlands']);

/**
 * The first country a fight in the open lands on in a biome and season: the head of the forge's `grounds()`
 * (crates/fk-schema/src/season.rs; copied in `sets.test.ts`). A place with no `country` of its own (the Gallery,
 * an older stage row) stands in it.
 */
export function firstCountry(biome: string | undefined, season: string | undefined): string {
  const s = season?.toLowerCase();
  switch (biome?.toLowerCase()) {
    case 'wetland': return 'mirewood';
    case 'boreal': return s === 'winter' ? 'winterwood' : 'pinewood';
    case 'desert': return 'dunes';
    case 'highland': return s === 'winter' ? 'winterwood' : 'highland';
    case 'jungle': return 'jungle';
    case 'underdark': return 'underdark';
    case 'ashlands': return 'ashlands';
    default: return s === 'autumn' ? 'autumn' : s === 'winter' ? 'winterwood' : 'forest';
  }
}

/**
 * The country set a place's set borrows from (EA14): the board's `country` (else the biome's first) in the place
 * set's biome, season and time of day, by `chooseSet`'s fallbacks. Undefined for a country's own set.
 */
export function countrySetFor(setId: string, country: string | undefined, avail: ReadonlySet<string> = setIdsAvailable()): string | undefined {
  const [kit, biome, season, time] = setId.split('/');
  if (!kit || !biome || COUNTRY_KITS.has(kit)) return undefined;
  const c = country && COUNTRY_KITS.has(country.toLowerCase()) ? country.toLowerCase() : firstCountry(biome, season);
  const id = chooseSet({ kit: c, biome, ...(season ? { season } : {}), time: time === 'night' ? 'night' : 'day' }, avail)?.id;
  return id === setId ? undefined : id;
}

/** Whether a board has a square a place may have to borrow its country's ground for: water, deep, rough, thicket, ice. */
export function boardNeedsCountry(board: { tiles: ReadonlyArray<{ terrain: string; ice?: boolean } | undefined> }): boolean {
  return board.tiles.some((t) => !!t && (t.terrain === 'Water' || t.terrain === 'Deep' || t.terrain === 'Bad' || t.terrain === 'Thicket' || !!t.ice));
}
